import * as CookieConsent from 'vanilla-cookieconsent'

export interface CookieConsentText {
  acceptAllBtn: string
  acceptNecessaryBtn: string
  analyticsDescription: string
  analyticsTitle: string
  closeIconLabel: string
  description: string
  footer: string
  googleAnalyticsDescription: string
  googleAnalyticsTitle: string
  moreInfoDescription: string
  moreInfoTitle: string
  necessaryDescription: string
  necessaryTitle: string
  preferencesTitle: string
  privacyChoicesDescription: string
  privacyChoicesTitle: string
  savePreferencesBtn: string
  serviceCounterLabel: string
  showPreferencesBtn: string
  tableHeaderDescription: string
  tableHeaderDomain: string
  tableHeaderName: string
  tableTitle: string
  title: string
}

const locales = ['en', 'sv', 'da', 'no', 'es']

const defaultText: CookieConsentText = {
  acceptAllBtn: 'Accept all',
  acceptNecessaryBtn: 'Reject all',
  analyticsDescription:
    'These cookies collect information about how you use our website. All of the data is anonymized and cannot be used to identify you.',
  analyticsTitle: 'Performance and Analytics',
  closeIconLabel: 'Close modal',
  description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua',
  footer: `<a href="/privacy" target="_blank">Privacy Policy</a>`,
  googleAnalyticsDescription: 'For user interactions',
  googleAnalyticsTitle: 'Google Analytics',
  moreInfoDescription: 'For any queries in relation to my policy on cookies and your choices, please <a href="/contact">contact us</a>',
  moreInfoTitle: 'More information',
  necessaryDescription: 'These cookies are essential for the proper functioning of the website and cannot be disabled.',
  necessaryTitle: 'Strictly Necessary',
  preferencesTitle: 'Manage cookie preferences',
  privacyChoicesDescription: `In this panel you can express some preferences related to the processing of your personal information. You may review and change expressed choices at any time by resurfacing this panel via the provided link. To deny your consent to the specific processing activities described below, switch the toggles to off or use the "Reject all" button and confirm you want to save your choices.`,
  privacyChoicesTitle: 'Your Privacy Choices',
  savePreferencesBtn: 'Accept current selection',
  serviceCounterLabel: 'Service|Services',
  showPreferencesBtn: 'Manage Individual preferences',
  tableHeaderDescription: 'Description',
  tableHeaderDomain: 'Domain',
  tableHeaderName: 'Cookie',
  tableTitle: 'Cookie table',
  title: 'We use cookies',
}

const CAT_NECESSARY = 'necessary'
const CAT_ANALYTICS = 'analytics'
export const SERVICE_ANALYTICS_STORAGE = 'analytics_storage'

// Set default consent to 'denied' (this should happen before changing any other dataLayer)

function updateGtagConsent() {
  if (window && window.gtag) {
    window.gtag('consent', 'update', {
      [SERVICE_ANALYTICS_STORAGE]: CookieConsent.acceptedService(SERVICE_ANALYTICS_STORAGE, CAT_ANALYTICS) ? 'granted' : 'denied',
    })
  }
}

export const getConfig = ({
  acceptAllBtn,
  acceptNecessaryBtn,
  analyticsDescription,
  analyticsTitle,
  closeIconLabel,
  description,
  footer,
  googleAnalyticsDescription,
  googleAnalyticsTitle,
  moreInfoDescription,
  moreInfoTitle,
  necessaryDescription,
  necessaryTitle,
  preferencesTitle,
  privacyChoicesDescription,
  privacyChoicesTitle,
  savePreferencesBtn,
  serviceCounterLabel,
  showPreferencesBtn,
  tableHeaderDescription,
  tableHeaderDomain,
  tableHeaderName,
  tableTitle,
  title,
}: CookieConsentText = defaultText) => {
  const translation: CookieConsent.Translation = {
    consentModal: {
      title: title,
      description: description,
      acceptAllBtn: acceptAllBtn,
      acceptNecessaryBtn: acceptNecessaryBtn,
      showPreferencesBtn: showPreferencesBtn,
      footer: footer,
    },
    preferencesModal: {
      title: preferencesTitle,
      acceptAllBtn: acceptAllBtn,
      acceptNecessaryBtn: acceptNecessaryBtn,
      savePreferencesBtn: savePreferencesBtn,
      closeIconLabel: closeIconLabel,
      serviceCounterLabel: serviceCounterLabel,
      sections: [
        {
          title: privacyChoicesTitle,
          description: privacyChoicesDescription,
        },
        {
          title: necessaryTitle,
          description: necessaryDescription,
          linkedCategory: 'necessary',
        },
        {
          title: analyticsTitle,
          description: analyticsDescription,
          linkedCategory: 'analytics',
          cookieTable: {
            caption: tableTitle,
            headers: {
              name: tableHeaderName,
              domain: tableHeaderDomain,
              desc: tableHeaderDescription,
            },
            body: [
              {
                name: '_ga',
                domain: location.hostname,
                desc: googleAnalyticsDescription,
              },
            ],
          },
        },
        {
          title: moreInfoTitle,
          description: moreInfoDescription,
        },
      ],
    },
  }
  const config: CookieConsent.CookieConsentConfig = {
    // root: 'body',
    // autoShow: true,
    // disablePageInteraction: true,
    // hideFromBots: true,
    // mode: 'opt-in',
    // revision: 0,

    cookie: {
      // name: 'cc_cookie',
      // domain: location.hostname,
      // path: '/',
      // sameSite: "Lax",
      // expiresAfterDays: 365,
    },
    onFirstConsent: () => {
      updateGtagConsent()
    },
    onConsent: () => {
      updateGtagConsent()
    },
    onChange: () => {
      updateGtagConsent()
    },

    onModalReady: () => {},

    onModalShow: () => {},

    onModalHide: () => {},

    // https://cookieconsent.orestbida.com/reference/configuration-reference.html#guioptions
    guiOptions: {
      consentModal: {
        layout: 'box inline',
        position: 'bottom left',
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box',
        equalWeightButtons: true,
        flipButtons: false,
      },
    },

    categories: {
      [CAT_NECESSARY]: {
        enabled: true, // this category is enabled by default
        readOnly: true, // this category cannot be disabled
      },
      [CAT_ANALYTICS]: {
        autoClear: {
          cookies: [
            {
              name: /^_ga/, // regex: match all cookies starting with '_ga'
            },
          ],
        },

        // https://cookieconsent.orestbida.com/reference/configuration-reference.html#category-services
        services: {
          [SERVICE_ANALYTICS_STORAGE]: {
            label: googleAnalyticsTitle,
            onAccept: () => {},
            onReject: () => {},
          },
        },
      },
      ads: {},
    },

    language: {
      default: 'en',
      translations: locales.reduce<Record<string, CookieConsent.Translation>>((acc, locale) => {
        acc[locale] = translation
        return acc
      }, {}),
    },
  }

  return config
}
